import type {
  AskHelenaConversationMessage,
  AskHelenaMessage,
  AskHelenaMessageFeedback,
  AskHelenaPendingWriteStatus,
} from "shared";
import { debugConsole } from "utilz/helpers";

const PENDING_WRITE_STATUSES: Record<
  string,
  AskHelenaPendingWriteStatus | undefined
> = {
  pending: "pending",
  confirmed: "confirmed",
  cancelled: "cancelled",
  expired: "expired",
  superseded: "superseded",
};

const parsePendingWriteStatus = (
  value: string,
): AskHelenaPendingWriteStatus => {
  const status = PENDING_WRITE_STATUSES[value];
  if (status) return status;

  debugConsole("Ask Helena: unknown pendingWrite status", value);
  return "pending";
};

const TERMINAL_TURN_STATUSES = new Set([
  "completed",
  "stopped",
  "timed_out",
  "failed",
  "interrupted",
]);

/**
 * Whether the stored thread is finished, so it is safe to replace what is on
 * screen with it.
 *
 * Reads the LAST USER message: `turn` is null on assistant messages, and only
 * the last question's turn can still be running. Absent means the turn predates
 * the field, which is only ever true of a finished one.
 */
export const isAskHelenaThreadSettled = (
  messages: AskHelenaConversationMessage[],
): boolean => {
  const question = [...messages].reverse().find((m) => m.role === "user");
  if (!question) return false;

  const status = question.turn?.status;
  if (!status) return true;
  if (TERMINAL_TURN_STATUSES.has(status)) return true;

  if (status !== "running") {
    debugConsole("Ask Helena: unknown turn status", status);
  }
  return false;
};

const toFeedback = (
  feedback: AskHelenaMessageFeedback | null | undefined,
): AskHelenaMessageFeedback | null => {
  if (feedback?.rating === "up" || feedback?.rating === "down") {
    return feedback;
  }

  if (feedback) debugConsole("Ask Helena: unknown feedback rating", feedback);
  return null;
};

/**
 * One stored message, in the shape the live stream produces — so a rehydrated
 * thread and a streamed one share a renderer.
 *
 * createdAt is dropped: the thread renders in arrival order.
 */
export const toAskHelenaMessage = (
  message: AskHelenaConversationMessage,
): AskHelenaMessage => {
  const pendingWrite = message.pendingWrite;

  return {
    id: message.id,
    role: message.role,
    content: message.content,
    serverMessageId: message.id,
    feedback: toFeedback(message.feedback),
    ticketRefs: message.ticketRefs ?? null,
    ...(pendingWrite && {
      pendingWrite: {
        pendingWriteId: pendingWrite.id,
        label: pendingWrite.label,
        summary: pendingWrite.summary,
        arguments: pendingWrite.arguments,
        status: parsePendingWriteStatus(pendingWrite.status),
      },
      isPendingWriteResolved: !pendingWrite.actionable,
    }),
  };
};
